import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listCustomers, createCustomer, deleteCustomer, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Customer } from '../types';

export default function CustomersPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ companyName: '', contactName: '', contactEmail: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  function load() {
    listCustomers()
      .then((res) => setCustomers(res.customers))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load customers.'));
  }

  useEffect(load, []);

  async function handleDelete(c: Customer) {
    if (!confirm(`Permanently delete ${c.companyName} and all their data? This cannot be undone.`)) return;
    if (!confirm('This is irreversible. Confirm again to proceed.')) return;
    setDeletingId(c.customerId);
    setError(null);
    try {
      await deleteCustomer(c.customerId);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete customer.');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await createCustomer(form);
      setForm({ companyName: '', contactName: '', contactEmail: '', notes: '' });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create customer.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>Customers</h1>
        {isAdmin && (
          <button className="primary" onClick={() => setShowForm((v) => !v)} style={{ flex: 'none' }}>
            {showForm ? 'Cancel' : 'New customer'}
          </button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {showForm && (
        <form className="card" onSubmit={handleCreate}>
          <div className="row">
            <div className="field">
              <label htmlFor="companyName">Company name</label>
              <input
                id="companyName"
                required
                value={form.companyName}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="contactName">Contact name</label>
              <input
                id="contactName"
                required
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="contactEmail">Contact email</label>
            <input
              id="contactEmail"
              type="email"
              required
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="notes">Notes</label>
            <textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create customer'}
          </button>
        </form>
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Status</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {customers
              .filter((c) => !c.archived)
              .map((c) => (
                <tr key={c.customerId}>
                  <td>
                    <Link to={`/customers/${c.customerId}`}>{c.companyName}</Link>
                  </td>
                  <td>{c.contactName}</td>
                  <td>{c.contactEmail}</td>
                  <td>{c.status}</td>
                  {isAdmin && (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => navigate(`/customers/${c.customerId}`)}
                        style={{ minHeight: 32, padding: '4px 10px' }}
                      >
                        Edit
                      </button>{' '}
                      <button
                        className="danger"
                        onClick={() => handleDelete(c)}
                        disabled={deletingId === c.customerId}
                        style={{ minHeight: 32, padding: '4px 10px' }}
                      >
                        {deletingId === c.customerId ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
